/**
 * 通用模态框（正式 ui-* 组件之一）。
 *
 * 用 Radix 而非手写遮罩，是为了拿到我们真正需要但手写容易漏的行为：
 * 焦点陷阱、Esc 关闭、滚动锁定、aria 角色与对话框语义。
 * 专业工具里模态框出现频率很高，这些细节会长期影响手感。
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, Bell, OctagonAlert, Info, X } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * 弹窗语义调性。四种弹窗共用一个容器，**只改强调条与图标颜色**，
 * 不允许各业务自己写一套背景/圆角/阴影 —— 那是“弹窗风格不统一”的根因。
 *
 * 颜色全部走 token（方案 B 已实算对比度），不写硬编码 hex。
 */
export type ModalTone = 'neutral' | 'info' | 'warning' | 'danger';

const TONE: Record<ModalTone, { bar: string; icon: string; Icon: typeof Info }> = {
  /**
   * 四态必须“颜色 + 图标形状”两个维度都互异（浏览器实测发现初版
   * 信息与警告同色、中性与信息同图标，导致实际只有三种可辨）。
   * 颜色：灰 / 冷蓝 / 橙 / 红；形状：铃 / 圆i / 三角 / 八边。
   * token 统一走 state-* 系列，neutral 不再借用文本色，避免将来改文本色时连带影响弹窗。
   */
  neutral: { bar: 'bg-state-neutral', icon: 'text-state-neutral', Icon: Bell },
  info: { bar: 'bg-state-info', icon: 'text-state-info', Icon: Info },
  warning: { bar: 'bg-state-warn', icon: 'text-state-warn', Icon: AlertTriangle },
  danger: { bar: 'bg-state-danger', icon: 'text-state-danger', Icon: OctagonAlert },
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** 标题下的一句话说明（如影响范围声明） */
  summary: string;
  children: ReactNode;
  /** 底部操作区，由调用方决定按钮语义 */
  footer: ReactNode;
  /** 默认 neutral；info 用于功能说明，warning 用于能力受限，danger 用于不可逆操作确认 */
  tone?: ModalTone;
  /**
   * 摆位：center=居中对话框（默认）；right=右侧满高抽屉。
   * 适用于“选完还要看主区效果”的浏览型面板（如姿势库）——居中会盖住视口。
   * 只改容器位置/尺寸，背景/圆角/阴影/色调仍共用同一套，不破统一风格。
   */
  placement?: 'center' | 'right';
}

export function Modal({
  open,
  onClose,
  title,
  summary,
  children,
  footer,
  tone = 'neutral',
  placement = 'center',
}: ModalProps) {
  const t = TONE[tone];
  const Icon = t.Icon;
  const isRight = placement === 'right';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogPrimitive.Portal>
        {/*
          遮罩必须给不透明黑色：浮层是 portal 到 body 的，
          若不显式给背景，会透明叠在页面上看起来“配色不统一”。
        */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <DialogPrimitive.Content
          className={
            isRight
              ? 'fixed right-0 top-0 z-50 flex h-full w-[min(460px,94vw)] flex-col overflow-hidden border-l border-surface-line bg-surface-overlay p-5 shadow-[0_2px_10px_rgba(0,0,0,.5)] focus:outline-none'
              : 'fixed left-1/2 top-1/2 z-50 w-[min(560px,92vw)] max-h-[80vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-card border border-surface-line bg-surface-overlay p-5 shadow-[0_2px_10px_rgba(0,0,0,.5)] focus:outline-none'
          }
          aria-describedby={undefined}
        >
          <div className={isRight ? 'mb-3 flex shrink-0 items-start justify-between gap-4' : 'mb-3 flex items-start justify-between gap-4'}>
            <div className="flex min-w-0 items-start gap-2.5">
              {/* 左侧语义条 + 图标：不靠颜色单独传递信息，标题文字仍说明性质 */}
              <span className={['mt-0.5 h-4 w-[3px] shrink-0 rounded-full', t.bar].join(' ')} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icon size={15} className={['shrink-0', t.icon].join(' ')} />
                  <DialogPrimitive.Title className="text-ui font-semibold">
                    {title}
                  </DialogPrimitive.Title>
                </div>
                <DialogPrimitive.Description className="mt-1 text-uiSm text-ink-muted">
                  {summary}
                </DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close
              className="flex h-7 min-w-7 shrink-0 cursor-pointer items-center justify-center
                         rounded-control p-1 text-ink-muted transition-colors duration-150
                         hover:bg-surface-raised hover:text-ink-primary"
              aria-label="关闭"
            >
              <X size={16} />
            </DialogPrimitive.Close>
          </div>

          {isRight ? (
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          ) : (
            children
          )}

          <div className={isRight ? 'mt-5 flex shrink-0 flex-wrap gap-2' : 'mt-5 flex flex-wrap gap-2'}>{footer}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
